import { Alert } from 'antd';
import { useEffect, useState } from 'react';
import * as React from 'react';
import SecondaryProgramCourseApiService from '../../../api/SecondaryProgramCourseApiService';
import Routes from '../../../config/Routes';
import EducationProgramReportDataTableProviderDTO from '../../../models/EducationProgramReportDataTableProviderDTO';
import TableFilterUtil, { TableFilterOption } from '../../../utils/TableFilterUtil';
import DataTable, { FilterType } from '../core/DataTable';
import DataTableButtonUtil from '../core/DataTableButtonUtil';
import TableRequestDTO from '../core/models/TableRequestDTO';

interface EducationProgramReportDataTableProps {
  academicYear: number;
  fromSelfStudies?: boolean;
  fromAcademicYearDataReview?: boolean;
  isPublic?: boolean;
  districtId?: string;
  errors?: string[];
  configuration?: boolean;
}

function EducationProgramReportDataTable(props: EducationProgramReportDataTableProps) {
  const [errors] = useState(props.errors);
  const [institutions, setInstitutions] = useState([] as TableFilterOption[]);

    useEffect(() => {
      TableFilterUtil.Institutions()
        .then(x => {
          setInstitutions(x);
        });
    }, []);

  const getColumnDefinitions = () => {
    const columns: any = [];

    columns.pushAll([
      {
        title: 'Program Identifier',
        dataIndex: EducationProgramReportDataTableProviderDTO.secondaryProgramId,
        sorter: true,
        width: 50,
        filterType: FilterType.Text,
      },
      {
        title: 'District',
        dataIndex: EducationProgramReportDataTableProviderDTO.districtId,
        sorter: true,
        render: (data: string, row: EducationProgramReportDataTableProviderDTO) => {
          return row.district?.display;
        },
        filterType: FilterType.Text,
      },
      {
        title: 'CIP Number',
        dataIndex: EducationProgramReportDataTableProviderDTO.secondaryCipNumber,
        sorter: true,
        render: (data: string, row: EducationProgramReportDataTableProviderDTO) => {
          return row.secondaryCipNumber?.code + ' - ' + row.secondaryCipNumber?.description;
        },
        filterType: FilterType.Text,
      },
      {
        title: 'Program Start Year',
        dataIndex: EducationProgramReportDataTableProviderDTO.programStartYear,
        sorter: true,
        filterType: FilterType.Text,
      },
      {
        title: 'Program End Year',
        dataIndex: EducationProgramReportDataTableProviderDTO.programEndYear,
        sorter: true,
        filterType: FilterType.Text,
      },
      {
        title: 'Course Identifier',
        dataIndex: EducationProgramReportDataTableProviderDTO.secondaryCourseId,
        sorter: true,
        filterType: FilterType.Text,
      },
      {
        title: 'Course Name',
        dataIndex: EducationProgramReportDataTableProviderDTO.courseName,
        sorter: true,
        filterType: FilterType.Text,
      },
      {
        title: 'Local Course Number',
        dataIndex: EducationProgramReportDataTableProviderDTO.localCourseNumber,
        sorter: true,
        filterType: FilterType.Text,
      },
      {
        title: 'SCED',
        dataIndex: EducationProgramReportDataTableProviderDTO.sced,
        sorter: true,
        filterType: FilterType.Text,
      },
      {
        title: 'Carnegie Unit',
        dataIndex: EducationProgramReportDataTableProviderDTO.carnegieUnit,
        sorter: true,
        filterType: FilterType.Text,
      },
      {
        title: 'Community College',
        dataIndex: EducationProgramReportDataTableProviderDTO.communityCollegeId,
        sorter: true,
        width: 150,
        render: (data: string, row: EducationProgramReportDataTableProviderDTO) => {
          return row.communityCollege?.display;
        },
        filterType: FilterType.DropdownMulti,
        dropdownFilterOptions: institutions
      },
      {
        title: 'Institution Course',
        dataIndex: EducationProgramReportDataTableProviderDTO.institutionCourseId,
        sorter: true,
        width: 150,
        render: (data: string, row: EducationProgramReportDataTableProviderDTO) => {
          if (row.institutionCourse) {
            return row.institutionCourse?.course?.discipline?.code + '' + row.institutionCourse?.course?.catalogNumber + ' - ' + row.institutionCourse?.currentDetail?.title;
          }
        },
        filterType: FilterType.Text
      },
      {
        title: 'Enrollment',
        dataIndex: EducationProgramReportDataTableProviderDTO.enrollment,
        sorter: true,
        filterType: FilterType.Text,
      },
    ]);

    return columns;
  };

  const educationProgramReport = () => {
    window.open(
      Routes.generateFull(Routes.SELF_STUDY_EDUCATION_PROGRAM_REPORT, {}, { academicYear: props.academicYear }),
      '_blank'
    );
  };

  const actionButtons = [];
  actionButtons.push(DataTableButtonUtil.Reset());
  actionButtons.push(DataTableButtonUtil.Default('Export', educationProgramReport));

  const renderErrors = () => {
    if (errors && errors.length > 0) {
      return <Alert showIcon={true} type="error" description={renderErrorMessages()} message="Error Importing" />;
    }
  };

  const renderErrorMessages = () => {
    if (errors && errors.length > 0) {
      return (
        <>
          {errors.map((e: any) => (
            <p key={e}>{e}</p>
          ))}
        </>
      );
    }
  };

  return (
    <>
      {renderErrors()}
      <DataTable
        serverSide={true}
        tableProps={{
          rowKey: 'id',
          scroll: { x: 500 },
        }}
        globalSearch={true}
        buttonBar={actionButtons}
        columns={getColumnDefinitions()}
        fetchData={{
          fetch: (requestState: TableRequestDTO) =>
            SecondaryProgramCourseApiService.getEducationProgramReportTableData(requestState, props.academicYear),
        }}
        stateSaving={{ enabled: true, tableUniqueKey: 'secondary_program_course_list' }}
      />
    </>
  );
}

export default EducationProgramReportDataTable;
